import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard,
  BookOpen,
  Brain,
  Sparkles,
  XCircle,
  Star,
  Crown, Gem,
  GraduationCap,
  Shield,
  Settings,
  LogOut,
  Menu,
  X,
  Mic,
  Volume2,
  BookText,
} from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_italian-med-test/artifacts/5mezey23_Logo%20MPI.jpeg";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/training", label: "Allenamento", icon: BookOpen, testId: "nav-training" },
  { to: "/review", label: "Ripasso SRS", icon: Brain, testId: "nav-review" },
  { to: "/ssm", label: "Stile SSM", icon: Sparkles, testId: "nav-ssm" },
  { to: "/mistakes", label: "I miei errori", icon: XCircle, testId: "nav-mistakes" },
  { to: "/favorites", label: "Preferiti", icon: Star, testId: "nav-favorites" },
  { to: "/premium", label: "Premium", icon: Crown, testId: "nav-premium" },
  { to: "/vip", label: "Academia VIP", icon: GraduationCap, testId: "nav-vip" },
  { to: "/vip/oral-coach", label: "Coach Orale · IA", icon: Volume2, testId: "nav-vip-oral-coach" },
  { to: "/aims", label: "Pacchetto AIMS", icon: BookText, testId: "nav-aims" },
  { to: "/account/preferences", label: "Preferenze", icon: Settings, testId: "nav-account-preferences" },
];

function BrandMark({ size = "md", onClick }) {
  const dim = size === "lg" ? "h-11 w-11" : "h-9 w-9";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="brand-mark"
      className="flex items-center gap-3 group"
    >
      <span className={`${dim} rounded-full bg-white border border-brand-border overflow-hidden flex items-center justify-center shrink-0 shadow-premium transition-transform duration-200 group-hover:scale-105`}>
        <img src={LOGO} alt="" className="h-full w-full object-cover scale-[1.6] -translate-y-[6%]" />
      </span>
      <span className="text-left">
        <span className="block font-serif text-base font-semibold tracking-tight text-brand-primary leading-none">Medicos para Italia</span>
        <span className="block text-[10px] uppercase tracking-[0.22em] text-brand-secondary mt-1">Esame · Italia</span>
      </span>
    </button>
  );
}

function NavItem({ to, label, icon: Icon, testId, onClick, accent = "green" }) {
  const accentBar = accent === "gold" ? "bg-brand-gold" : "bg-brand-green";
  const accentText = accent === "gold" ? "text-brand-gold" : "text-brand-green";
  return (
    <NavLink
      to={to}
      data-testid={testId}
      onClick={onClick}
      className={({ isActive }) =>
        [
          "group relative flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors duration-200",
          isActive
            ? "bg-brand-green/10 text-brand-primary font-medium"
            : "text-brand-secondary hover:bg-brand-surfaceElevated hover:text-brand-primary",
        ].join(" ")
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <motion.span
              layoutId="active-pill"
              className={`absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r-full ${accentBar}`}
              transition={{ type: "spring", stiffness: 500, damping: 35 }}
            />
          )}
          <Icon className={`h-4 w-4 ${isActive ? accentText : ""}`} />
          <span>{label}</span>
        </>
      )}
    </NavLink>
  );
}

/**
 * VIPNavItem — premium animated CTA for the Academia VIP entry in the sidebar.
 *
 * Visual treatment:
 *  - Animated red→gold gradient that breathes (4s cycle)
 *  - Pulsing glow ring around the item
 *  - Crown icon with subtle shimmer
 *  - "EXCLUSIVO" badge
 *  - Hover scale + brighter glow
 *
 * Implementation notes:
 *  - We animate a `linear-gradient` background-position rather than 'background'
 *    directly (cheaper to GPU-rasterize, smoother).
 *  - `prefers-reduced-motion` is respected via the framer-motion default.
 *  - When the route is active, glow stays steady at gold so the user knows
 *    they are inside the academy.
 */
function VIPNavItem({ to, testId, onClick }) {
  return (
    <NavLink
      to={to}
      data-testid={testId}
      onClick={onClick}
      className="vip-nav-link group relative block"
    >
      {({ isActive }) => (
        <div
          className={[
            "relative flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold overflow-hidden",
            "transition-all duration-300 ease-out",
            "shadow-[0_4px_14px_rgba(220,38,38,0.25)] hover:shadow-[0_8px_24px_rgba(234,179,8,0.45)]",
            "hover:scale-[1.03] active:scale-[0.99]",
          ].join(" ")}
          style={{
            background: isActive
              ? "linear-gradient(135deg, #B45309 0%, #D97706 50%, #B45309 100%)"
              : "linear-gradient(135deg, #B91C1C 0%, #DC2626 25%, #EAB308 50%, #DC2626 75%, #B91C1C 100%)",
            backgroundSize: isActive ? "100% 100%" : "300% 300%",
            color: "#FFF8E7",
            animation: isActive ? "none" : "vip-shimmer 4.5s ease-in-out infinite",
            border: "1px solid rgba(255, 220, 130, 0.35)",
          }}
        >
          {/* Pulsing glow ring */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 rounded-xl"
            style={{
              boxShadow: isActive
                ? "0 0 0 0 rgba(234, 179, 8, 0)"
                : "0 0 16px 2px rgba(234, 179, 8, 0.45)",
              animation: isActive ? "none" : "vip-pulse 2.4s ease-in-out infinite",
            }}
          />
          {/* Animated highlight sweep */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -inset-1 rounded-xl opacity-60 group-hover:opacity-100 transition-opacity"
            style={{
              background:
                "linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.35) 50%, transparent 70%)",
              backgroundSize: "200% 100%",
              animation: "vip-sweep 3.2s linear infinite",
              mixBlendMode: "overlay",
            }}
          />
          {/* Icon — crown with subtle wobble on hover */}
          <span className="relative z-10 inline-flex items-center justify-center h-7 w-7 rounded-lg shrink-0"
                style={{ background: "rgba(255, 248, 231, 0.18)",
                         border: "1px solid rgba(255, 248, 231, 0.3)" }}>
            <Crown className="h-4 w-4 transition-transform duration-300 group-hover:rotate-[-6deg] group-hover:scale-110"
                   style={{ filter: "drop-shadow(0 0 4px rgba(255,255,255,0.6))" }} />
          </span>
          {/* Label */}
          <span className="relative z-10 flex flex-col leading-tight">
            <span className="font-serif tracking-tight text-[15px]"
                  style={{ textShadow: "0 1px 2px rgba(0,0,0,0.25)" }}>
              Academia VIP
            </span>
            <span className="text-[9px] uppercase tracking-[0.22em] opacity-90">
              Esclusivo · Premium
            </span>
          </span>
          {/* "Exclusivo" mini-badge */}
          <span className="relative z-10 ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-bold"
                style={{ background: "rgba(255, 248, 231, 0.92)", color: "#7A1D1D" }}>
            <Gem className="h-2.5 w-2.5" /> VIP
          </span>
        </div>
      )}
    </NavLink>
  );
}

function UserStrip({ user, onLogout }) {
  return (
    <div className="p-3 border-t border-brand-border">
      <div className="flex items-center gap-3 px-2 py-2">
        {user?.picture ? (
          <img src={user.picture} alt="" className="h-9 w-9 rounded-full border border-brand-border" />
        ) : (
          <div className="h-9 w-9 rounded-full bg-brand-green/15 text-brand-green flex items-center justify-center text-sm font-semibold">
            {(user?.name || "U")[0]}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-brand-primary truncate">{user?.name || "Utente"}</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-brand-secondary truncate">
            {user?.is_premium ? "Premium" : "Free plan"}
          </div>
        </div>
        <button
          onClick={onLogout}
          data-testid="logout-btn"
          className="btn-tactile p-2 rounded-lg text-brand-secondary hover:text-brand-red hover:bg-brand-red/10 transition-colors"
          title="Esci"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/**
 * SimulazioneNavItem — neutral sidebar item for the unified Simulazione
 * Reale hub. Pablo asked to remove the red attention styling so only
 * Academia VIP stays visually prominent.
 */
function SimulazioneNavItem({ to, testId, onClick }) {
  return (
    <NavLink
      to={to}
      data-testid={testId}
      onClick={onClick}
      className={({ isActive }) =>
        [
          "group flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors",
          isActive
            ? "bg-brand-primary/8 text-brand-primary font-semibold"
            : "text-brand-secondary hover:bg-brand-primary/5 hover:text-brand-primary",
        ].join(" ")
      }
    >
      <Mic className="h-4 w-4 shrink-0" />
      <span className="leading-tight">Simulazione Reale</span>
    </NavLink>
  );
}

function Sidebar({ user, navigate, onClick, onLogout }) {
  return (
    <aside className="h-full w-72 border-r border-brand-border bg-brand-surface flex flex-col">
      <div className="h-20 px-5 flex items-center border-b border-brand-borderSubtle">
        <BrandMark size="lg" onClick={() => { navigate("/dashboard"); onClick?.(); }} />
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        <div className="eyebrow px-4 mt-2 mb-2">Studio</div>
        {navItems.slice(0, 3).map((it) => (
          <NavItem key={it.to} {...it} onClick={onClick} />
        ))}
        {/* Attention-grabbing red simulator entry — sits at the TOP of the
            sidebar so it is unmissable; pulses red when the user is on it. */}
        <div className="pt-0.5 pb-0.5">
          <SimulazioneNavItem to="/vip/oral"
                              testId="nav-vip-oral"
                              onClick={onClick} />
        </div>
        {navItems.slice(3, 6).map((it) => (
          <NavItem key={it.to} {...it} onClick={onClick} />
        ))}
        {/* Premium animated VIP entry — makes the academy the most visually
            salient option in the whole sidebar. */}
        <div className="pt-1 pb-1">
          <VIPNavItem to="/vip" testId="nav-vip" onClick={onClick} />
        </div>
        <div className="eyebrow px-4 mt-5 mb-2">Account</div>
        {navItems.slice(6).filter(it => it.to !== "/vip").map((it) => (
          <NavItem key={it.to} {...it} accent="gold" onClick={onClick} />
        ))}
        {user?.is_admin && (
          <>
            <NavItem
              to="/admin"
              label="Admin"
              icon={Shield}
              testId="nav-admin"
              onClick={onClick}
              accent="gold"
            />
            <NavItem
              to="/admin/vip"
              label="Admin VIP"
              icon={Shield}
              testId="nav-admin-vip"
              onClick={onClick}
              accent="gold"
            />
          </>
        )}
      </nav>
      <UserStrip user={user} onLogout={onLogout} />
    </aside>
  );
}

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen flex bg-brand-background text-brand-primary">
      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-30 h-16 px-4 glass-panel flex items-center justify-between">
        <BrandMark onClick={() => navigate("/dashboard")} />
        <motion.button
          whileTap={{ scale: 0.92 }}
          data-testid="mobile-menu-btn"
          onClick={() => setOpen(true)}
          className="btn-tactile p-2 rounded-lg text-brand-primary hover:bg-brand-surfaceElevated"
        >
          <Menu className="h-6 w-6" />
        </motion.button>
      </header>

      {/* Desktop sidebar */}
      <div className="hidden lg:block sticky top-0 h-screen">
        <Sidebar user={user} navigate={navigate} onLogout={logout} />
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <div className="lg:hidden fixed inset-0 z-50 flex">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="absolute inset-0 bg-brand-primary/40 backdrop-blur-sm"
              onClick={() => setOpen(false)}
            />
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 320, damping: 32 }}
              className="relative h-full"
            >
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="absolute top-4 right-4 z-10 btn-tactile p-2 rounded-lg text-brand-secondary hover:bg-brand-surfaceElevated"
              >
                <X className="h-5 w-5" />
              </button>
              <Sidebar
                user={user}
                navigate={navigate}
                onClick={() => setOpen(false)}
                onLogout={logout}
              />
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <main className="flex-1 min-w-0 pt-16 lg:pt-0">
        {children}
      </main>
    </div>
  );
}
